import { useDebouncedValue } from '@tanstack/react-pacer'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { organisationProductsQueryOptions } from '@/api/resources/organisation-products'
import { organisationQueryOptions } from '@/api/resources/organisations'
import { deleteRevenuePricingRule, upsertRevenuePricingRule } from '@/api/resources/pricing'
import { OrganisationNavSelect } from '@/components/layout/organisation-nav-select'
import { PageHeader } from '@/components/layout/page-header'
import { ProductsList, ProductsListSkeleton } from '@/components/products/products-list'
import { SearchInput } from '@/components/ui/search-input'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/products/$organisationId')({
  head: () => ({ meta: [{ title: 'Products - MyJunea' }] }),
  beforeLoad: async ({ params, context }) => {
    const organisationId = Number(params.organisationId)
    if (Number.isNaN(organisationId)) {
      throw redirect({ to: '/products' })
    }

    const organisation = await context.queryClient.ensureQueryData(
      organisationQueryOptions(organisationId),
    )

    return { organisation }
  },
  component: ProductsOrganisationPage,
})

function ProductsOrganisationPage(): React.ReactElement {
  const queryClient = useQueryClient()
  const { organisationId: organisationIdParam } = Route.useParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })

  const organisationId = Number(organisationIdParam)

  const {
    data: products,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
  } = useQuery({
    ...organisationProductsQueryOptions(organisationId, {
      search: debouncedSearch || undefined,
    }),
    placeholderData: keepPreviousData,
  })

  const organisationPriceMutation = useMutation({
    mutationFn: async ({ productId, price }: { productId: number; price: number }) =>
      upsertRevenuePricingRule({ organisation_id: organisationId, product_id: productId, price }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationProducts.all(organisationId),
      })
      toast.success('Price updated')
    },
    onError: () => {
      toast.error('Failed to update price')
    },
  })

  const organisationPriceResetMutation = useMutation({
    mutationFn: async (productId: number) =>
      deleteRevenuePricingRule({ organisation_id: organisationId, product_id: productId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationProducts.all(organisationId),
      })
      toast.success('Price reset to default')
    },
    onError: () => {
      toast.error('Failed to reset price')
    },
  })

  const machinePriceMutation = useMutation({
    mutationFn: async ({
      machineCode,
      productId,
      price,
    }: {
      machineCode: string
      productId: number
      price: number
    }) => upsertRevenuePricingRule({ machine_code: machineCode, product_id: productId, price }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationProducts.all(organisationId),
      })
      toast.success('Machine price updated')
    },
    onError: () => {
      toast.error('Failed to update machine price')
    },
  })

  const machinePriceResetMutation = useMutation({
    mutationFn: async ({ machineCode, productId }: { machineCode: string; productId: number }) =>
      deleteRevenuePricingRule({ machine_code: machineCode, product_id: productId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationProducts.all(organisationId),
      })
      toast.success('Machine price reset to default')
    },
    onError: () => {
      toast.error('Failed to reset machine price')
    },
  })

  const isPending = (productId: number): boolean =>
    (organisationPriceMutation.isPending &&
      organisationPriceMutation.variables?.productId === productId) ||
    (organisationPriceResetMutation.isPending &&
      organisationPriceResetMutation.variables === productId) ||
    (machinePriceMutation.isPending && machinePriceMutation.variables?.productId === productId) ||
    (machinePriceResetMutation.isPending &&
      machinePriceResetMutation.variables?.productId === productId)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Products"
        description="View and manage product pricing across your machines."
        defaultFiltersOpen={true}
        filters={
          <>
            <OrganisationNavSelect
              currentOrganisationId={organisationId}
              baseRoute="/products/$organisationId"
            />
            <SearchInput
              placeholder="Search products..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="w-48"
            />
          </>
        }
      />

      {isProductsLoading ? (
        <ProductsListSkeleton />
      ) : (
        <div className={isProductsFetching ? 'opacity-60 transition-opacity duration-200' : ''}>
          <ProductsList
            products={products ?? []}
            priceEdit={{
              onSaveOrganisation: (productId, price) =>
                organisationPriceMutation.mutate({ productId, price }),
              onResetOrganisation: (productId) => organisationPriceResetMutation.mutate(productId),
              onSaveMachine: (machineCode, productId, price) =>
                machinePriceMutation.mutate({ machineCode, productId, price }),
              onResetMachine: (machineCode, productId) =>
                machinePriceResetMutation.mutate({ machineCode, productId }),
              isPending,
            }}
          />
        </div>
      )}
    </div>
  )
}
